import { ApolloLink } from '@apollo/client/core';

// Lazy-loaded DataDog SDK reference (avoid importing in test environments)
let datadogLogs = null;

// Configuration
// Client token (safe for browser) - from DataDog > Organization Settings > Client Tokens
// Default is Jeff's personal DataDog free-tier account for POC/testing
// SkillRev POC/testing client token:
// const DEFAULT_CLIENT_TOKEN = 'pub88c7da0d79fea6b67c13f033fcc8c474';
const DEFAULT_CLIENT_TOKEN = 'puba2de3627d9948397b12ebd37e1761ff2'; // Regov local OK Prod token
const CLIENT_TOKEN = process.env.VUE_APP_DATADOG_CLIENT_TOKEN || DEFAULT_CLIENT_TOKEN;
const SITE = process.env.VUE_APP_DATADOG_SITE || 'us5.datadoghq.com';
const DUPLICATE_THRESHOLD_MS = 30000; // 30 seconds
const LONG_RUNNING_THRESHOLD_MS = 1500; // 1.5 seconds
const PAYLOAD_LOG_THRESHOLD_MS = 7000; // 7 seconds - log payload for very slow calls

// Recent requests cache for duplicate detection
// Map<hash, { timestamp, count, metadata }>
const recentRequests = new Map();

// Track if SDK is initialized
let sdkInitialized = false;

/**
 * Check if we're running in a test environment.
 */
function isTestEnvironment() {
  // Check for unit test flag set in tests/unit/util/mock-browser-oddities.js
  if (typeof global !== 'undefined' && global.isVueUnitTest) return true;
  // Check for Node.js test environment
  if (process.env.NODE_ENV === 'test') return true;
  return false;
}

/**
 * Initialize the DataDog Browser SDK.
 * Call this once at app startup.
 */
export function initDatadog() {
  if (sdkInitialized || !CLIENT_TOKEN) return;

  // Skip initialization in test environments to avoid SDK errors
  if (isTestEnvironment()) return;

  try {
    // Lazy load the DataDog SDK only when needed (avoids import issues in tests)
    // eslint-disable-next-line global-require, import/no-unresolved
    datadogLogs = require('@datadog/browser-logs').datadogLogs;

    datadogLogs.init({
      clientToken: CLIENT_TOKEN,
      site: SITE,
      service: 'tmc-local-frontend',
      env: window.location.hostname.includes('local') ? 'development' : 'production',
      forwardErrorsToLogs: false, // We handle errors ourselves
      sessionSampleRate: 100,
    });

    // Add hostname as global context for county environment identification
    datadogLogs.setGlobalContextProperty('hostname', window.location.hostname);

    sdkInitialized = true;
    // eslint-disable-next-line no-console
    console.log('[diagnostics] DataDog SDK initialized');
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[diagnostics] DataDog SDK init failed:', err.message);
  }
}

/**
 * Check if diagnostics are enabled.
 * Requires both:
 * 1. VUE_APP_DATADOG_CLIENT_TOKEN env var to be set
 * 2. devConfig.diagnosticsEnabled === true in SystemConfig
 */
function isEnabled() {
  if (!CLIENT_TOKEN) return false;
  try {
    /* eslint-disable global-require */
    // eslint-disable-next-line prefer-destructuring
    const store = require('../../store/index.js').default;
    /* eslint-enable global-require */
    const devConfig = store.state.SystemConfig.devSettingItem;
    return devConfig && devConfig.diagnosticsEnabled === true;
  } catch (e) {
    return false;
  }
}

/**
 * Safely extract payload information for logging.
 * Truncates large payloads and handles serialization errors gracefully.
 * @param {*} payload - The payload to extract info from
 * @param {number} [maxLength=2000] - Maximum length of serialized payload
 * @returns {Object} - { payloadSize, payloadPreview, payloadKeys }
 */
function safeExtractPayloadInfo(payload, maxLength = 2000) {
  const result = {
    payloadSize: null,
    payloadPreview: null,
    payloadKeys: null,
  };

  if (payload === undefined || payload === null) {
    return result;
  }

  try {
    // Get the keys if it's an object
    if (typeof payload === 'object' && !Array.isArray(payload)) {
      result.payloadKeys = Object.keys(payload);
    }

    // Serialize and measure size
    const serialized = JSON.stringify(payload);
    result.payloadSize = serialized.length;

    // Truncate if needed
    if (serialized.length <= maxLength) {
      result.payloadPreview = serialized;
    } else {
      result.payloadPreview = `${serialized.slice(0, maxLength)}... [truncated]`;
    }
  } catch (e) {
    // Handle circular references or other serialization errors
    result.payloadPreview = '[serialization error]';
  }

  return result;
}

/**
 * Simple djb2 hash function for request signatures.
 * Creates a consistent hash from method, url, and body.
 */
function hashRequest(method, url, body) {
  const str = `${method}|${url}|${JSON.stringify(body || '')}`;
  let hash = 5381;
  for (let i = 0; i < str.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    hash = ((hash << 5) + hash) + str.charCodeAt(i);
  }
  // eslint-disable-next-line no-bitwise
  return (hash >>> 0).toString(16);
}

/**
 * Clean up expired entries from the recent requests cache.
 */
function cleanupExpiredEntries() {
  const now = Date.now();
  const expiredKeys = [];
  recentRequests.forEach((value, key) => {
    if (now - value.timestamp > DUPLICATE_THRESHOLD_MS) {
      expiredKeys.push(key);
    }
  });
  expiredKeys.forEach((key) => recentRequests.delete(key));
}

/**
 * Check if this request hash is a duplicate (same hash within threshold).
 * Returns { isDuplicate, count } where count is how many times we've seen it.
 */
function checkDuplicate(hash) {
  cleanupExpiredEntries();
  const existing = recentRequests.get(hash);
  if (existing) {
    const timeSince = Date.now() - existing.timestamp;
    if (timeSince < DUPLICATE_THRESHOLD_MS) {
      return { isDuplicate: true, count: existing.count + 1 };
    }
  }
  return { isDuplicate: false, count: 1 };
}

/**
 * Track the start of a request.
 */
function trackRequestStart(hash, metadata) {
  const existing = recentRequests.get(hash);
  const count = existing ? existing.count + 1 : 1;
  recentRequests.set(hash, {
    timestamp: Date.now(),
    count,
    metadata,
  });
}

/**
 * Log a diagnostic event locally to the Vuex store.
 */
function logLocally(type, data) {
  try {
    /* eslint-disable global-require */
    // eslint-disable-next-line prefer-destructuring
    const store = require('../../store/index.js').default;
    /* eslint-enable global-require */
    let message;
    if (type === 'duplicate') {
      message = `Duplicate API call: ${data.operationName || data.url} (${data.count}x in ${DUPLICATE_THRESHOLD_MS / 1000}s)`;
    } else if (type === 'long-running') {
      message = `Slow API call: ${data.operationName || data.url} (${data.durationMs}ms)`;
    } else if (type === 'error') {
      message = `API Error [${data.client}]: ${data.message}`;
    } else if (type === 'info') {
      message = data.message;
    } else {
      message = `Diagnostic [${type}]: ${JSON.stringify(data)}`;
    }
    store.commit('appendRecentLog', ['diagnostics', message]);
  } catch (e) {
    // Ignore errors if store not available
  }
}

/**
 * Send a diagnostic event to DataDog via the Browser SDK.
 * Fire-and-forget - errors are logged locally but don't propagate.
 */
function sendToDatadog(type, data) {
  if (!sdkInitialized || !datadogLogs) return;

  try {
    let threshold = null;
    if (type === 'duplicate') threshold = DUPLICATE_THRESHOLD_MS;
    else if (type === 'long-running') threshold = LONG_RUNNING_THRESHOLD_MS;

    const context = {
      type,
      source: 'browser',
      regovAppLayer: 'frontend', // vs api, db etc
      client: data.client || null,
      operationName: data.operationName || null,
      url: data.url || null,
      path: data.path || null,
      hash: data.hash || null,
      duplicateCount: data.count || null,
      duration: data.durationMs ? data.durationMs * 1000000 : null,
      threshold: threshold ? threshold * 1000000 : null,
      errorStatus: data.status || null,
      errorDetails: data.details || null,
      // Payload info for very slow calls (> 7s)
      payloadSize: data.payloadSize || null,
      payloadPreview: data.payloadPreview || null,
      payloadKeys: data.payloadKeys || null,
    };

    let message;
    if (type === 'duplicate') {
      message = `Duplicate API call: ${data.operationName || data.url}`;
    } else if (type === 'long-running') {
      message = `Slow API call: ${data.operationName || data.url} (${data.durationMs}ms)`;
    } else if (type === 'error') {
      message = `API Error: ${data.message || 'Unknown error'}`;
    } else {
      message = `Diagnostic: ${type}`;
    }

    if (type === 'error') {
      datadogLogs.logger.error(message, context);
    } else {
      datadogLogs.logger.info(message, context);
    }
  } catch (err) {
    logLocally('error', { message: `DataDog send failed: ${err.message}`, client: 'datadog' });
  }
}

/**
 * Report a diagnostic event - logs locally and sends to DataDog.
 */
function reportDiagnostic(type, data) {
  // Log locally first (always works)
  logLocally(type, data);

  // Send to DataDog (fire-and-forget)
  sendToDatadog(type, data);
}

/**
 * Create an Apollo Link for GraphQL diagnostics.
 * Tracks duplicates and long-running requests.
 */
export function createDiagnosticsLink() {
  return new ApolloLink((operation, forward) => {
    // Early return if not enabled - minimal overhead
    if (!isEnabled()) {
      return forward(operation);
    }

    const operationName = operation.operationName || 'unknown';
    const hash = hashRequest('graphql', operationName, operation.variables);
    const startTime = Date.now();

    // Check for duplicate
    const { isDuplicate, count } = checkDuplicate(hash);
    trackRequestStart(hash, { client: 'graphql', operationName });

    if (isDuplicate) {
      reportDiagnostic('duplicate', {
        client: 'graphql',
        operationName,
        hash,
        count,
      });
    }

    // Forward the request and track completion
    return forward(operation).map((result) => {
      const durationMs = Date.now() - startTime;

      if (durationMs > LONG_RUNNING_THRESHOLD_MS) {
        const diagnosticData = {
          client: 'graphql',
          operationName,
          hash,
          durationMs,
        };

        // Include payload info for very slow calls
        if (durationMs > PAYLOAD_LOG_THRESHOLD_MS) {
          const payloadInfo = safeExtractPayloadInfo(operation.variables);
          diagnosticData.payloadSize = payloadInfo.payloadSize;
          diagnosticData.payloadPreview = payloadInfo.payloadPreview;
          diagnosticData.payloadKeys = payloadInfo.payloadKeys;
        }

        reportDiagnostic('long-running', diagnosticData);
      }

      return result;
    });
  });
}

/**
 * Attach diagnostics interceptors to an Axios instance.
 * Tracks duplicates and long-running requests.
 */
export function attachAxiosDiagnostics(axiosInstance) {
  // Request interceptor - track start time and check duplicates
  axiosInstance.interceptors.request.use((config) => {
    // Early return if not enabled
    if (!isEnabled()) {
      return config;
    }

    const hash = hashRequest(config.method, config.url, config.data);
    // eslint-disable-next-line no-param-reassign
    config._diagnosticsHash = hash;
    // eslint-disable-next-line no-param-reassign
    config._diagnosticsStart = Date.now();

    // Check for duplicate
    const { isDuplicate, count } = checkDuplicate(hash);
    trackRequestStart(hash, { client: 'rest', url: config.url });

    if (isDuplicate) {
      reportDiagnostic('duplicate', {
        client: 'rest',
        url: config.url,
        hash,
        count,
      });
    }

    return config;
  });

  // Response interceptor - track duration
  axiosInstance.interceptors.response.use(
    (response) => {
      // Early return if not enabled or no tracking data
      if (!isEnabled() || !response.config._diagnosticsStart) {
        return response;
      }

      const durationMs = Date.now() - response.config._diagnosticsStart;

      if (durationMs > LONG_RUNNING_THRESHOLD_MS) {
        const diagnosticData = {
          client: 'rest',
          url: response.config.url,
          hash: response.config._diagnosticsHash,
          durationMs,
        };

        // Include payload info for very slow calls
        if (durationMs > PAYLOAD_LOG_THRESHOLD_MS) {
          const payloadInfo = safeExtractPayloadInfo(response.config.data);
          diagnosticData.payloadSize = payloadInfo.payloadSize;
          diagnosticData.payloadPreview = payloadInfo.payloadPreview;
          diagnosticData.payloadKeys = payloadInfo.payloadKeys;
        }

        reportDiagnostic('long-running', diagnosticData);
      }

      return response;
    },
    (error) => {
      // Also track duration for errors
      if (isEnabled() && error.config && error.config._diagnosticsStart) {
        const durationMs = Date.now() - error.config._diagnosticsStart;

        if (durationMs > LONG_RUNNING_THRESHOLD_MS) {
          const diagnosticData = {
            client: 'rest',
            url: error.config.url,
            hash: error.config._diagnosticsHash,
            durationMs,
          };

          // Include payload info for very slow calls
          if (durationMs > PAYLOAD_LOG_THRESHOLD_MS) {
            const payloadInfo = safeExtractPayloadInfo(error.config.data);
            diagnosticData.payloadSize = payloadInfo.payloadSize;
            diagnosticData.payloadPreview = payloadInfo.payloadPreview;
            diagnosticData.payloadKeys = payloadInfo.payloadKeys;
          }

          reportDiagnostic('long-running', diagnosticData);
        }
      }

      return Promise.reject(error);
    },
  );
}

/**
 * Report an API error to diagnostics.
 * Call this from error handlers to send errors to DataDog.
 *
 * @param {Object} options - Error details
 * @param {string} options.client - 'graphql' or 'rest'
 * @param {string} options.message - Error message
 * @param {string} [options.operationName] - GraphQL operation name
 * @param {string} [options.url] - REST endpoint URL
 * @param {string} [options.path] - GraphQL path
 * @param {number} [options.status] - HTTP status code
 * @param {string} [options.details] - Additional error details
 */
export function reportError(options) {
  // Only report if diagnostics are enabled
  if (!isEnabled()) return;

  reportDiagnostic('error', {
    client: options.client,
    message: options.message,
    operationName: options.operationName || null,
    url: options.url || null,
    path: options.path || null,
    status: options.status || null,
    details: options.details || null,
  });
}

// Auto-initialize SDK when module loads
initDatadog();

export default {
  initDatadog,
  createDiagnosticsLink,
  attachAxiosDiagnostics,
  reportError,
  isEnabled,
};
